/*
 * useTier — single-flight React hook that reads /api/me/tier and
 * caches the response for the session.
 *
 * All PremiumLock UI (button lock badges, upgrade modal, sidebar
 * gating hints) reads through this hook so no screen refetches on
 * its own.
 *
 * Exported:
 *   useTier()              → { data, loading, refetch, hasFeature(k) }
 *   TierProvider           → optional top-level provider (App.js wraps once)
 *   FEATURE_LABELS         → user-friendly label per feature key
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const TierContext = createContext(null);

export const FEATURE_LABELS = {
  featured_submission:     "Featured placements",
  track_promotion:         "Track promotions",
  live_beam_create:        "Live Beams",
  creator_hub_premium:     "Creator Hub tools",
  beat_upload:             "Beat uploads",
  beat_marketplace_sell:   "Beat sales",
  beat_marketplace_lease:  "Beat leasing",
  premium_analytics:       "Premium analytics",
  ai_promo_tools:          "AI promo tools",
  earnings_dashboard:      "Earnings dashboard",
  advanced_sharing:        "Advanced sharing",
  contest_participate:     "Contest entries",
  business_hub:            "Business Hub",
  business_listing_create: "Business listings",
  producer_mode:           "Producer Mode",
  reel_record_freestyle:      "Record Freestyle Reels",
  reel_upload_video:          "Upload Video Reels",
  reel_image_with_music:      "Photo + Music Reels",
  reel_performance_own_track: "Promo / Performance Reels",
  reel_pick_own_track:        "Attach your RGM track",
};

const DEFAULT = {
  tier: "free",
  producer_mode: false,
  features: {},
  feature_min_tier: {},
  gates_enforced: false,
};

export const TierProvider = ({ children }) => {
  const [state, setState] = useState({ data: DEFAULT, loading: true });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/me/tier");
      setState({ data: { ...DEFAULT, ...(r.data || {}) }, loading: false });
    } catch (e) {
      // Unauthenticated visitors treat everything as locked.
      setState({ data: DEFAULT, loading: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const value = useMemo(() => ({
    ...state,
    refetch: load,
    hasFeature: (key) => Boolean(state.data?.features?.[key]),
    minTierFor: (key) => state.data?.feature_min_tier?.[key] || "premium_monthly",
    tier: state.data?.tier || "free",
    producerMode: !!state.data?.producer_mode,
    gatesEnforced: !!state.data?.gates_enforced,
  }), [state, load]);

  return <TierContext.Provider value={value}>{children}</TierContext.Provider>;
};

export const useTier = () => {
  const ctx = useContext(TierContext);
  if (ctx) return ctx;
  // Fallback for pages that render outside the provider — cheaper than
  // crashing the app.
  return {
    data: DEFAULT,
    loading: false,
    refetch: () => {},
    hasFeature: () => false,
    minTierFor: () => "premium_monthly",
    tier: "free",
    producerMode: false,
    gatesEnforced: false,
  };
};

export default useTier;
